import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Globe, Presentation } from 'lucide-react';

const Hero = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('office-space');

  const heroCategories = [
    {
      id: 'office-space',
      label: 'Office space',
      icon: Building2,
      route: '/office-space',
      image: './images-optimized/office Space.webp',
      title: 'Office space your way',
      subtitle: 'From a single seat to a private office — work your way'
    },
    {
      id: 'coworking',
      label: 'Coworking',
      icon: Users,
      route: '/coworking',
      image: './images-optimized/galery-images/IMG_E6807.webp',
      title: 'Coworking spaces',
      subtitle: 'Collaborative environments designed for productivity and networking.'
    },
    {
      id: 'virtual-offices',
      label: 'Virtual Offices',
      icon: Globe,
      route: '/virtual-office',
      image: './images-optimized/Virtual Office.webp',
      title: 'Virtual office solutions',
      subtitle: 'Professional business address and services without the overhead.'
    },
    {
      id: 'meeting-rooms',
      label: 'Meeting rooms',
      icon: Presentation,
      route: '/meeting-rooms',
      image: './images-optimized/galery-images/Meeting Room .webp',
      title: 'Meeting room facilities',
      subtitle: 'Professional meeting spaces equipped with modern technology.'
    }
  ];

  const currentCategory = heroCategories.find(cat => cat.id === activeCategory);

  const handleCategoryClick = (category) => {
    setActiveCategory(category.id);
    // Only change the background image, don't navigate
  };

  return (
    <div className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={currentCategory?.image}
          alt={currentCategory?.title}
          className="w-full h-full object-cover transition-all duration-700 ease-in-out"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center">
        <div className="max-w-7xl mx-auto px-4 text-center">
          {/* Hero Text */}
          <div className="mb-12">
            <p className="text-white/90 text-lg md:text-xl mb-4 font-medium tracking-wide">
              {currentCategory?.subtitle}
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight">
              {currentCategory?.title}
              <span className="text-[#e30613]">.</span>
            </h1>
          </div>

          {/* Category Navigation */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl p-1.5 max-w-2xl mx-auto shadow-xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
              {heroCategories.map((category) => {
                const IconComponent = category.icon;
                const isActive = activeCategory === category.id;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    className={`group relative overflow-hidden rounded-lg p-3 md:p-4 transition-all duration-300 transform hover:scale-105 ${
                      isActive
                        ? 'bg-[#e30613] text-white shadow-lg shadow-[#e30613]/30'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 hover:text-[#e30613]'
                    }`}
                  >
                    {/* Icon */}
                    <div className="mb-2">
                      <IconComponent 
                        size={24} 
                        className={`mx-auto transition-all duration-300 ${
                          isActive ? 'text-white' : 'text-gray-600 group-hover:text-[#e30613]'
                        }`} 
                      />
                    </div>
                    
                    {/* Label */}
                    <span className={`block font-semibold text-xs md:text-sm leading-tight ${
                      isActive ? 'text-white' : 'text-gray-700 group-hover:text-[#e30613]'
                    }`}>
                      {category.label}
                    </span>

                    {/* Active Indicator */}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/40"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12">
            <p className="text-white/80 text-lg mb-6">
              Ready to find your perfect workspace?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => navigate('/products')}
                className="bg-[#e30613] hover:bg-[#c1050f] text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Explore All Solutions
              </button>
              <button 
                onClick={() => navigate('/gallery')}
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 border border-white/20"
              >
                View Gallery
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default Hero;